import { useCallback, useEffect, useRef } from 'react';

const SHOPIFY_WEB_VITAL_NAMES = ['LCP', 'FCP', 'INP', 'CLS', 'TTFB'];

export default function ShopifyPerformanceMonitor({
  shopify,
  isEnabled,
  sendLog,
  activationDelayMs = 0,
}) {
  const mountedAtRef = useRef(Date.now());
  const isEnabledRef = useRef(isEnabled);
  const sendLogRef = useRef(sendLog);
  const queuedMetricsRef = useRef([]);
  const queuedMetricKeysRef = useRef(new Set());
  const queuedMonitorStatusRef = useRef(null);
  const sentMetricKeysRef = useRef(new Set());
  const earlyMetricsDrainedRef = useRef(false);
  const lcpObservedRef = useRef(false);
  const monitorStatusRef = useRef(null);

  useEffect(() => {
    isEnabledRef.current = isEnabled;
    sendLogRef.current = sendLog;
  }, [isEnabled, sendLog]);

  const getMetricKey = useCallback((metric) => `${metric.name}:${metric.id || Math.round(Number(metric.value) || 0)}`, []);
  const normalizeMetric = useCallback((metric) => {
    if (!metric) return null;
    const name = String(metric.name || metric.metricName || '').toUpperCase();
    if (!SHOPIFY_WEB_VITAL_NAMES.includes(name)) return null;
    const value = Number(metric.value ?? metric.delta);
    if (!Number.isFinite(value)) return null;
    return {
      ...metric,
      name,
      value,
      id: metric.id || metric.navigationId || `${name}-${Math.round(value)}`,
      source: metric.source || 'app_bridge_web_vitals',
      path: metric.path || (typeof window !== 'undefined' ? window.location?.pathname : null),
      visibilityState: metric.visibilityState || (typeof document !== 'undefined' ? document.visibilityState : null),
    };
  }, []);

  const getMetricRating = useCallback((name, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return undefined;
    if (name === 'LCP') return numericValue <= 2500 ? 'good' : numericValue <= 4000 ? 'needs-improvement' : 'poor';
    if (name === 'FCP') return numericValue <= 1800 ? 'good' : numericValue <= 3000 ? 'needs-improvement' : 'poor';
    if (name === 'TTFB') return numericValue <= 800 ? 'good' : numericValue <= 1800 ? 'needs-improvement' : 'poor';
    return undefined;
  }, []);

  const sendMetric = useCallback((metric) => {
    const normalizedMetric = normalizeMetric(metric);
    if (!normalizedMetric) return;
    const key = getMetricKey(normalizedMetric);
    if (sentMetricKeysRef.current.has(key)) return;
    sentMetricKeysRef.current.add(key);
    queuedMetricKeysRef.current.delete(key);
    const addOn = normalizedMetric.source || 'app_bridge_web_vitals';
    sendLogRef.current?.(
      `SHOPIFY_WEB_VITAL_${normalizedMetric.name}`,
      String(Math.round(Number(normalizedMetric.value) || 0)),
      addOn,
      JSON.stringify({
        id: normalizedMetric.id,
        name: normalizedMetric.name,
        value: normalizedMetric.value,
        rating: normalizedMetric.rating || getMetricRating(normalizedMetric.name, normalizedMetric.value),
        country: normalizedMetric.country,
        source: addOn,
        path: normalizedMetric.path,
        visibilityState: normalizedMetric.visibilityState,
        lcpTargetMs: 2500,
        overLcpTarget: normalizedMetric.name === 'LCP' ? Number(normalizedMetric.value) > 2500 : false,
      })
    );
  }, [getMetricKey, getMetricRating, normalizeMetric]);

  const queueOrSendMetric = useCallback((metric) => {
    const normalizedMetric = normalizeMetric(metric);
    if (!normalizedMetric) return;
    if (normalizedMetric.name === 'LCP') {
      lcpObservedRef.current = true;
    }
    const key = getMetricKey(normalizedMetric);
    if (sentMetricKeysRef.current.has(key) || queuedMetricKeysRef.current.has(key)) return;
    if (isEnabledRef.current && sendLogRef.current) {
      sendMetric(normalizedMetric);
      return;
    }
    queuedMetricKeysRef.current.add(key);
    queuedMetricsRef.current.push(normalizedMetric);
  }, [getMetricKey, normalizeMetric, sendMetric]);

  const sendMonitorStatus = useCallback((status, addOn = 'app_bridge_web_vitals') => {
    if (monitorStatusRef.current === status) return;
    if (!isEnabledRef.current || !sendLogRef.current) {
      queuedMonitorStatusRef.current = { status, addOn };
      return;
    }
    const pageAgeMs = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? Math.round(performance.now())
      : null;
    const visibilityState = typeof document !== 'undefined' ? document.visibilityState : null;
    monitorStatusRef.current = status;
    queuedMonitorStatusRef.current = null;
    sendLogRef.current(
      'SHOPIFY_WEB_VITAL_MONITOR_STATUS',
      status,
      addOn,
      JSON.stringify({
        status,
        source: addOn,
        path: typeof window !== 'undefined' ? window.location?.pathname : null,
        visibilityState,
        documentHidden: visibilityState ? visibilityState !== 'visible' : null,
        activationDelayMs,
        registeredAfterMs: Date.now() - mountedAtRef.current,
        pageAgeMs,
        lcpObserved: lcpObservedRef.current,
        lcpTargetMs: 2500,
        queuedMetricCount: queuedMetricsRef.current.length,
        browserObserverBuffered: addOn === 'browser_performance_observer',
      })
    );
  }, [activationDelayMs]);

  const makeBrowserMetric = useCallback((name, value, id) => ({
    name,
    value,
    id: id || `browser-${name}-${Math.round(Number(value) || 0)}`,
    rating: getMetricRating(name, value),
    source: 'browser_performance_observer',
  }), [getMetricRating]);

  const drainEarlyWebVitals = useCallback(() => {
    if (earlyMetricsDrainedRef.current || typeof window === 'undefined') return;
    earlyMetricsDrainedRef.current = true;
    const earlyWebVitals = window.__cartifindEarlyWebVitals;
    if (!earlyWebVitals) return;

    const metrics = typeof earlyWebVitals.drainMetrics === 'function'
      ? earlyWebVitals.drainMetrics()
      : [];
    metrics.forEach(queueOrSendMetric);

    const status = typeof earlyWebVitals.getStatus === 'function'
      ? earlyWebVitals.getStatus()
      : null;
    if (status) {
      sendMonitorStatus(
        status.lcpObserved ? 'early_lcp_observed' : status.status,
        status.source || 'early_browser_performance_observer'
      );
    }

    if (typeof earlyWebVitals.disconnect === 'function') {
      earlyWebVitals.disconnect();
    }
  }, [queueOrSendMetric, sendMonitorStatus]);

  const registerBrowserPerformanceObserver = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.PerformanceObserver === 'undefined') {
      sendMonitorStatus('fallback_unavailable', 'browser_performance_observer');
      return null;
    }
    const observers = [];
    const observe = (type, handler) => {
      try {
        const observer = new window.PerformanceObserver((list) => {
          handler(list.getEntries());
        });
        observer.observe({ type, buffered: true });
        observers.push(observer);
      } catch (_error) {
        // Some embedded browser runtimes do not support every observer type.
      }
    };

    observe('largest-contentful-paint', (entries = []) => {
      entries.forEach((entry) => {
        const value = entry.renderTime || entry.loadTime || entry.startTime;
        queueOrSendMetric(makeBrowserMetric('LCP', value, entry.id || entry.url || entry.startTime));
      });
    });
    observe('paint', (entries = []) => {
      entries
        .filter((entry) => entry.name === 'first-contentful-paint')
        .forEach((entry) => {
          queueOrSendMetric(makeBrowserMetric('FCP', entry.startTime, entry.name));
        });
    });
    try {
      const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
      if (navigationEntry?.responseStart) {
        queueOrSendMetric(makeBrowserMetric('TTFB', navigationEntry.responseStart, 'navigation-response-start'));
      }
    } catch (_error) {
      // Navigation timing is optional evidence; LCP remains the required sample.
    }

    if (observers.length === 0) {
      sendMonitorStatus('fallback_unavailable', 'browser_performance_observer');
      return null;
    }
    sendMonitorStatus('fallback_registered', 'browser_performance_observer');
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [makeBrowserMetric, queueOrSendMetric, sendMonitorStatus]);

  useEffect(() => {
    if (!isEnabled || !sendLog) return;
    drainEarlyWebVitals();
    if (queuedMonitorStatusRef.current) {
      const queuedStatus = queuedMonitorStatusRef.current;
      sendMonitorStatus(queuedStatus.status, queuedStatus.addOn);
    }
    const queuedMetrics = queuedMetricsRef.current.splice(0);
    queuedMetrics.forEach(sendMetric);
  }, [drainEarlyWebVitals, isEnabled, sendLog, sendMetric, sendMonitorStatus]);

  useEffect(() => {
    let cancelled = false;
    let unregister = null;
    let fallbackUnregister = null;
    let fallbackTimer = null;
    let lcpMissingTimer = null;

    const scheduleLcpMissingStatus = () => {
      if (lcpMissingTimer || cancelled) return;
      lcpMissingTimer = window.setTimeout(() => {
        if (!cancelled && !lcpObservedRef.current) {
          sendMonitorStatus('lcp_missing_after_timeout');
        }
      }, 9000);
    };

    const startFallbackObserver = () => {
      if (fallbackUnregister || cancelled) return;
      const maybeFallbackUnregister = registerBrowserPerformanceObserver();
      if (!cancelled && typeof maybeFallbackUnregister === 'function') {
        fallbackUnregister = maybeFallbackUnregister;
      }
      scheduleLcpMissingStatus();
    };

    const registerWebVitals = async () => {
      try {
        drainEarlyWebVitals();
        const webVitalsApi = shopify?.webVitals || shopify?.features?.webVitals;
        if (!webVitalsApi?.onReport) {
          sendMonitorStatus('unavailable');
          startFallbackObserver();
          scheduleLcpMissingStatus();
          return;
        }
        const maybeUnregister = await webVitalsApi.onReport((report) => {
          const metrics = Array.isArray(report?.metrics) ? report.metrics : [];
          metrics.forEach(queueOrSendMetric);
        });
        sendMonitorStatus('registered');
        scheduleLcpMissingStatus();
        if (!cancelled && typeof maybeUnregister === 'function') {
          unregister = maybeUnregister;
        }
        fallbackTimer = window.setTimeout(() => {
          if (!lcpObservedRef.current) {
            startFallbackObserver();
          }
        }, 4000);
      } catch (_error) {
        sendMonitorStatus('registration_error');
        startFallbackObserver();
        // Performance monitoring must never block the merchant-facing app shell.
      }
    };

    registerWebVitals();

    return () => {
      cancelled = true;
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      if (lcpMissingTimer) window.clearTimeout(lcpMissingTimer);
      if (typeof unregister === 'function') unregister();
      if (typeof fallbackUnregister === 'function') fallbackUnregister();
      try {
        const webVitalsApi = shopify?.webVitals || shopify?.features?.webVitals;
        webVitalsApi?.onReport?.(null);
      } catch (_error) {
        // Ignore cleanup errors from older App Bridge runtimes.
      }
    };
  }, [drainEarlyWebVitals, shopify, queueOrSendMetric, registerBrowserPerformanceObserver, sendMonitorStatus]);

  return null;
}
